import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, Share2, Upload, History, Link2, ScanText, CheckCheck } from 'lucide-react';
import notificationService from '../../services/notificationService';
import useClickOutside from '../../hooks/useClickOutside';
import Avatar from '../common/Avatar';
import { formatRelative } from '../../utils/format';
import cn from '../../utils/cn';

const ICONS = { share: Share2, upload: Upload, version: History, link: Link2, ocr: ScanText };

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const popRef = useRef(null);

  const load = useCallback(() => {
    notificationService.list().then(setItems).catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useClickOutside([btnRef, popRef], () => setOpen(false), open);

  const unread = items.filter((n) => !n.readAt).length;

  const toggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 8, left: Math.max(8, rect.right - 340) });
    }
    setOpen((v) => !v);
  };

  const openItem = async (n) => {
    setOpen(false);
    if (!n.readAt) {
      await notificationService.markRead(n.id);
      load();
    }
    if (n.resourceType === 'document') navigate(`/documents/${n.resourceId}/preview`);
    else if (n.resourceType === 'folder') navigate(`/documents/${n.resourceId}`);
  };

  const markAll = async () => {
    await notificationService.markAllRead();
    load();
  };

  return (
    <>
      <button ref={btnRef} type="button" className="btn--icon bell" onClick={toggle} aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`}>
        <Bell size={18} />
        {unread > 0 && <span className="bell__dot">{unread > 9 ? '9+' : unread}</span>}
      </button>

      {open &&
        createPortal(
          <div ref={popRef} className="pop" style={{ top: pos.top, left: pos.left }}>
            <header className="pop__header">
              <strong style={{ fontSize: 'var(--fs-cell)' }}>Notifications</strong>
              {unread > 0 && (
                <button type="button" className="toast__action" style={{ marginLeft: 'auto', fontSize: 'var(--fs-meta)' }} onClick={markAll}>
                  <CheckCheck size={13} style={{ display: 'inline', marginRight: 4, verticalAlign: -2 }} />
                  Mark all read
                </button>
              )}
            </header>

            <div className="pop__body">
              {items.length === 0 && (
                <p className="meta" style={{ padding: 'var(--sp-5)', textAlign: 'center' }}>
                  You&rsquo;re all caught up.
                </p>
              )}
              {items.slice(0, 8).map((n) => {
                const Icon = ICONS[n.type] || Bell;
                return (
                  <button
                    key={n.id}
                    type="button"
                    className="card-row"
                    style={{ background: n.readAt ? undefined : 'var(--brand-50)' }}
                    onClick={() => openItem(n)}
                  >
                    {n.actor ? (
                      <Avatar name={n.actor.name} />
                    ) : (
                      <span className="stat-card__icon">
                        <Icon size={15} />
                      </span>
                    )}
                    <span className="stack" style={{ gap: 2, flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: 'var(--fs-cell)', lineHeight: 1.4 }} className={cn(!n.readAt && 'strong')}>
                        {n.title}
                      </span>
                      {n.body && <span className="meta truncate">{n.body}</span>}
                      <span className="meta">{formatRelative(n.createdAt)}</span>
                    </span>
                  </button>
                );
              })}
            </div>

            <footer className="pop__footer">
              <Link to="/notifications" className="menu__item" onClick={() => setOpen(false)} style={{ justifyContent: 'center' }}>
                View all notifications
              </Link>
            </footer>
          </div>,
          document.body,
        )}
    </>
  );
}
